import classnames from 'classnames/bind'
import { isEmpty } from 'lodash-es'
import moment from 'moment'
import { useTranslation } from 'next-i18next'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import { useEffect, useRef, useState } from 'react'
import { useCookies } from 'react-cookie'
import TagManager from 'react-gtm-module'
import { ToastContainer } from 'react-toastify'
import { install } from 'resize-observer'

// Libs
import { isLiveMallClub } from '@/constants/systemType'
import { useAppLayout } from '@/hooks/useAppLayout'
import { useAppTheme } from '@/hooks/useAppTheme'
import { useAuthorization } from '@/hooks/useAuthorization'
import useCart from '@/hooks/useCart'
import useFixedPlayerByStorage from '@/hooks/useFixedPlayerByStorage'
import useFullScreen from '@/hooks/useFullScreen'
import { useLayout } from '@/hooks/useLayout'
import { useMemberData } from '@/hooks/useMemberData'
import usePreviousRoute from '@/hooks/usePreviousRoute'
import { useRefreshToken } from '@/hooks/useRefreshToken'
import useSearchHotKey from '@/hooks/useSearchHotKey'
import useTimerClear from '@/hooks/useTimerClear'
import fastCheckout from '@/methods/fastCheckout'
import { handleCartDataUpdate } from '@/methods/handleCartDataUpdate'
import handleLogoutReturnToOriginalPath from '@/methods/handleLogoutReturnToOriginalPath'
import getSearchParams from '@/methods/url/getSearchParams'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import { setIsOverseaUser, setLocationData } from '@/redux/reducers/common'
import { resetMemberData } from '@/redux/reducers/member'
import { updateSelectorStatus } from '@/redux/reducers/shoppingCart'
import { checkLogMode } from '@/utils/log'
import { handleSentryEvent } from '@/methods/handleSentryEvent'

// Components
import Loading from '@/components/common/Loading'
import Popup from '@/components/common/Popup'
import PortalWithPlayer from '@/components/common/PortalWithPlayer'
import Debug from './components/Debug'

// Constants
import { CartAction } from '@/constants/cart'
import { VALID_BRAND_NAME } from '@/constants/brand'
import { envConfigs } from '@/constants/config'
import { COOKIE_DATA, COOKIE_OPTIONS, socialReferralCookieKey, SocialReferralKey } from '@/constants/cookieKey'
import { COUNTRY_CODE } from '@/constants/countryCode'
import { LIVE_SOURCE_TYPE, ROOM_LIVE_TYPE } from '@/constants/liveRoom'
import { ROUTE_PATH } from '@/constants/routePath'
import { STORAGE_DATA } from '@/constants/storageData'

// Styles
import styles from './style.module.scss'

const cx = classnames.bind(styles)

const FixedPlayer = dynamic(() => import('@/components/shared/FixedPlayer'), {
  ssr: false,
})
const IGExternal = dynamic(() => import('./components/IGExternal'), {
  ssr: false,
})

const NO_LAYOUT_PAGES = new Set([ROUTE_PATH.LOGIN, ROUTE_PATH.REGISTER, ROUTE_PATH.FORGOT_PASSWORD])

const MyApp = ({ Component, ...pageProps }: any) => {
  const { t } = useTranslation()

  const videoRef = useRef(null)
  const kickoffPopupRef = useRef(null)
  const networkErrorPopupRef = useRef(null)

  //NOTE: 不要誤用 useRouter 的 pathname
  //TODO: 將來整合 redux SSR 後，可將 pathname 交由 redux 管理，把 props drilling 的 pathname 移除
  const { pathname } = pageProps.router
  const { brandName, isWebView } = pageProps.pageProps

  const [isCloseFixedPlayer, setIsCloseFixedPlayer] = useState(false)
  const [isShowIGExternal, setIsShowIGExternal] = useState(true)

  const fixedPlayerData = useAppSelector((state) => state.common?.fixedPlayerData)
  const memberData = useAppSelector((state) => state.member?.memberData)
  const roomData = useAppSelector((state) => state.room?.roomData)
  const socketData = useAppSelector((state) => state.socket)
  const showLoading = useAppSelector((state) => state.common.showLoading)
  const selectorStatus = useAppSelector((state) => state.shoppingCart?.selectorStatus)
  const logList = useAppSelector((state) => state.log?.logList)

  const isKickoff = socketData.isKickoff
  const isNetworkError = socketData.isNetworkError

  const dispatch = useAppDispatch()

  const isTestMode = getSearchParams()?.testMode === '1'
  const isIG = typeof window !== 'undefined' && window.navigator.userAgent.includes('Instagram')
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [pchomeUuidCookies, setPchomeUuidCookie, removePchomeUuidCookie] = useCookies([COOKIE_DATA.PCHOME_UUID])
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [pchomeNameExpiredCookies, setNameExpiredCookie, removeNameExpiredCookie] = useCookies([COOKIE_DATA.PCHOME_NAME_EXPIRED])

  const { memberInfoId } = memberData

  const isLive = roomData.liveType === ROOM_LIVE_TYPE.CURRENT
  const isPast = roomData.liveType === ROOM_LIVE_TYPE.PAST
  const Layout = useAppLayout({ brandName })
  useAppTheme({ brandName })

  useSearchHotKey({})

  const router = useRouter()
  usePreviousRoute()

  const { isReady, query } = router
  const countryCodeFromQuery = query?.country_code
  const socialReferralId = query?.[SocialReferralKey.socialReferralId]
  const socialReferralFrom = query?.[SocialReferralKey.socialReferralFrom]

  useAuthorization()
  useMemberData()
  useRefreshToken()
  useLayout()
  useCart()
  useFixedPlayerByStorage()
  useTimerClear()

  const isLiveRoomPath =
    pathname === ROUTE_PATH.LIVE_ROOM ||
    pathname === ROUTE_PATH.STRAWBERRY_LIVE_ROOM ||
    pathname === ROUTE_PATH.BRAND_LIVE_ROOM ||
    pathname === ROUTE_PATH.LIVE_ROOM_PRODUCT
  const isNotShoppingCartPage = pathname !== ROUTE_PATH.SHOPPINGCART
  const isNoLayoutPage = NO_LAYOUT_PAGES.has(pathname)

  const isMP4 = fixedPlayerData?.liveSourceType === LIVE_SOURCE_TYPE.MP4
  const fixedPlayerSrc = isMP4 ? fixedPlayerData?.liveSource : fixedPlayerData?.liveUrlInfo[0]?.streamUrl
  const isHostPlayerFlv = fixedPlayerData?.hostMemberInfoId === memberData?.memberInfoId

  const showFixedPlayer = !!fixedPlayerSrc && !isLiveRoomPath && !isHostPlayerFlv && !isCloseFixedPlayer && (isLive || isPast)

  const [, setCookie] = useCookies([socialReferralCookieKey])

  // 若為不合法的路徑(該系統內不存在的頁面)，則導回首頁
  useEffect(() => {
    if (isReady && !isEmpty(pathname)) {
      const isValidRoutePath = Object.values(ROUTE_PATH).find((routePath) => routePath === pathname)

      if (!isValidRoutePath) {
        window.location.href = `${envConfigs.domainUrl}${ROUTE_PATH.HOME}`
      }
    }
  }, [isReady, pathname])

  useEffect(() => {
    if (socialReferralFrom && socialReferralId) {
      setCookie(socialReferralCookieKey, { socialReferralFrom, socialReferralId }, { path: '/', maxAge: 14400, domain: window.location.hostname })
    }
  }, [isReady])

  useEffect(() => {
    if (!isEmpty(window)) {
      checkLogMode(dispatch)
    }
  }, [dispatch])

  useEffect(() => {
    // 舊瀏覽器不支援 ResizeObserver，所以另外安裝 resize-observer
    if (!window.ResizeObserver) {
      install()
    }
  }, [])

  useEffect(() => {
    if (envConfigs.GTM_ID) {
      TagManager.initialize({ gtmId: envConfigs.GTM_ID })
    }
  }, [envConfigs.GTM_ID])

  const { isFullScreen } = useFullScreen()

  useEffect(() => {
    // 如果 localstorage FAST_CHECKOUT_CACHE_DATA 有東西，代表要執行立即購買登入回來的流程
    if (memberInfoId && !isEmpty(window)) {
      const fastCheckoutCacheData: any = window.localStorage.getItem(STORAGE_DATA.FAST_CHECKOUT_CACHE_DATA)

      if (!isEmpty(fastCheckoutCacheData)) {
        const merchantId = JSON.parse(fastCheckoutCacheData)?.merchantId
        const skuId = JSON.parse(fastCheckoutCacheData)?.skuId
        const quantity = JSON.parse(fastCheckoutCacheData)?.quantity
        const roomId = JSON.parse(fastCheckoutCacheData)?.roomId
        const referralFrom = JSON.parse(fastCheckoutCacheData)?.referralFrom
        const referralId = JSON.parse(fastCheckoutCacheData)?.referralId

        const fastCheckoutItemPayload = {
          merchantId,
          skuId,
          quantity,
        }
        const skuList = [
          {
            skuId,
            quantity,
            roomId,
            referralFrom,
            referralId,
          },
        ]

        window.localStorage.removeItem(STORAGE_DATA.FAST_CHECKOUT_CACHE_DATA)
        handleCartDataUpdate({
          t,
          dispatch,
          action: CartAction.ADD,
          skuList,
          successCallback: () => fastCheckout({ router, dispatch, fastCheckoutItem: fastCheckoutItemPayload }),
        })
      }
    }
  }, [memberInfoId])

  useEffect(() => {
    if (isLiveRoomPath) setIsCloseFixedPlayer(false)
  }, [isLiveRoomPath])

  useEffect(() => {
    if (!isEmpty(selectorStatus)) {
      dispatch(updateSelectorStatus({}))
    }
  }, [isNotShoppingCartPage])

  useEffect(() => {
    // 如果 _document.tsx 內的 <Script/> strategy 改成其他的就要在 script 的 onLoad 處理

    try {
      const locationData = {
        _dittoCountryCode: window?._dittoCountryCode,
        _dittoCountryName: window?._dittoCountryName,
        _dittoContinentName: window._dittoContinentName,
        _dittoCurrency: window._dittoCurrency,
        _dittoLocale: window?._dittoLocale.replace('_', '-'),
        _dittoGTMContainer: window._dittoGTMContainer,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      }

      const locationDataFromSessionStorage = JSON.parse(window.sessionStorage.getItem(STORAGE_DATA?.LOCATION_DATA) || '{}')
      if (isReady) {
        if (isEmpty(locationDataFromSessionStorage) || moment().isSameOrAfter(locationDataFromSessionStorage.expiredAt)) {
          // sessionStorage 空的就拿 window 的 ditto location data 做事
          const isOverseaUser = countryCodeFromQuery || window._dittoCountryCode !== COUNTRY_CODE.TW

          const countryCode = countryCodeFromQuery ? countryCodeFromQuery : window._dittoCountryCode

          dispatch(setIsOverseaUser({ isOverseaUser }))
          dispatch(setLocationData({ locationData: { ...locationData, _dittoCountryCode: countryCode } }))

          window.sessionStorage.setItem(STORAGE_DATA.LOCATION_DATA, JSON.stringify({ ...locationData, expiredAt: moment().add(2, 'h') }))
        } else {
          // sessionStorage 不是空的就拿 sessionStorage 的  locationData 做事
          const isOverseaUser = countryCodeFromQuery || locationDataFromSessionStorage?._dittoCountryCode !== COUNTRY_CODE.TW

          const countryCode = countryCodeFromQuery ? countryCodeFromQuery : locationDataFromSessionStorage._dittoCountryCode

          dispatch(setIsOverseaUser({ isOverseaUser: isOverseaUser }))
          dispatch(setLocationData({ locationData: { ...locationDataFromSessionStorage, _dittoCountryCode: countryCode } }))
        }
      }
    } catch (error: any) {
      console.error('error', error)

      handleSentryEvent({
        message: error,
        level: 'error',
      })
    }
  }, [isReady])

  // PChome 合作案
  useEffect(() => {
    // 只有當品牌是 PChome 時才執行此邏輯
    const isPChomeBrand = brandName === VALID_BRAND_NAME.PCHOME

    if (pathname === ROUTE_PATH.BRAND_LIVE_ROOM && isPChomeBrand) {
      if (isEmpty(pchomeUuidCookies[COOKIE_DATA.PCHOME_UUID])) {
        const isDataFetched = !isEmpty(roomData.roomId) && memberData.isFetched

        if (isDataFetched) {
          const isHost = memberData?.memberInfoId === roomData?.memberInfoId

          if (!isHost) {
            dispatch(resetMemberData())
            localStorage.removeItem(STORAGE_DATA.TOKEN)
            // Jim 指定這裡要導頁 passport logout
            handleLogoutReturnToOriginalPath({ router })
          }
        }
      }
    } else if (pathname !== ROUTE_PATH.BRAND_LIVE_ROOM || !isPChomeBrand) {
      if (!isEmpty(pchomeUuidCookies[COOKIE_DATA.PCHOME_UUID])) {
        const isDataFetched = !isEmpty(roomData.roomId) && memberData.isFetched

        if (isDataFetched) {
          const isHost = memberData?.memberInfoId === roomData?.memberInfoId

          if (!isHost) {
            dispatch(resetMemberData())
            localStorage.removeItem(STORAGE_DATA.TOKEN)
            removePchomeUuidCookie(COOKIE_DATA.PCHOME_UUID, { path: '/', maxAge: 18316800, domain: COOKIE_OPTIONS.PCHOME.DOMAIN })
            removeNameExpiredCookie(COOKIE_DATA.PCHOME_NAME_EXPIRED, {
              path: '/',
              domain: COOKIE_OPTIONS.PCHOME.DOMAIN,
              expires: new Date(new Date().getTime() + 2 * 60 * 60 * 1000),
            })
            // Jim 指定這裡要導頁 passport logout
            handleLogoutReturnToOriginalPath({ router })
          }
        }
      }
    }
  }, [pathname, brandName, memberData.isFetched, roomData.roomId])

  return (
    <div id='app' data-theme='basic' className={cx('app', 'app-theme')}>
      {isNoLayoutPage ? (
        <>
          <Component {...pageProps} />
          <ToastContainer
            position={isLiveMallClub ? 'bottom-center' : 'top-center'}
            autoClose={1000}
            hideProgressBar
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable={false}
            pauseOnHover
            theme='light'
            icon={false}
            closeButton={false}
            style={{ bottom: '86px' }}
          />
        </>
      ) : (
        <Layout isWebView={isWebView} isFlutterApp={isWebView} pathname={pathname}>
          <Component {...pageProps} />

          {showFixedPlayer && (
            <FixedPlayer
              videoRef={videoRef}
              roomId={fixedPlayerData?.roomId}
              src={fixedPlayerSrc}
              setIsCloseFixedPlayer={setIsCloseFixedPlayer}
              isShow={showFixedPlayer}
              isPast={isPast}
              hostMemberInfoId={fixedPlayerData?.hostMemberInfoId}
              coverPhotoUrl={fixedPlayerData?.coverPhotoUrl}
            />
          )}

          {isTestMode && logList?.length > 0 && <Debug logList={logList} />}

          {isIG && isShowIGExternal && <IGExternal setIsShowIGExternal={setIsShowIGExternal} />}

          <Popup
            ref={kickoffPopupRef}
            title={t('LiveRoom_KickoffPopup_Title')}
            confirmLabel={t('Common_Ok')}
            onConfirm={() => window.location.reload()}
            isOpen={isKickoff}
            hasCancel={false}
            canClose={false}
          />

          <Popup
            ref={networkErrorPopupRef}
            title={t('Common_Network_Error')}
            confirmLabel={t('Common_Ok')}
            onConfirm={() => window.location.reload()}
            isOpen={isNetworkError}
            hasCancel={false}
            canClose={false}
          />

          {showLoading && <Loading />}

          <PortalWithPlayer isFullScreen={isFullScreen}>
            <ToastContainer
              position={isLiveMallClub ? 'bottom-center' : 'top-center'}
              autoClose={1000}
              hideProgressBar
              newestOnTop={false}
              closeOnClick
              rtl={false}
              pauseOnFocusLoss
              draggable={false}
              pauseOnHover
              theme='light'
              icon={false}
              closeButton={false}
              style={isLiveMallClub ? { bottom: '86px' } : undefined}
            />
          </PortalWithPlayer>
        </Layout>
      )}
    </div>
  )
}

export default MyApp
